import { Auth } from "aws-amplify";
import { Button, Card, Container, Row } from "react-bootstrap";
import {
  ActionFunction,
  Link,
  LoaderFunction,
  useLoaderData,
  useParams,
  useSubmit,
  useActionData,
} from "react-router-dom";
import {
  TableOptions,
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React from "react";
import { ArrowLeftCircleFill } from "react-bootstrap-icons";
import {
  BootstrapTable,
  PurchaseOrderItemMessagingHeader,
  SendPurchaseOrderItemMessageForm,
} from "../../../_components";
import {
  getMessagesForPurchaseOrderItem,
  getPurchaseOrderItem,
  sendPurchaseOrderItemMessage,
} from "../../../_gateway/vendorGateway";
import {
  PurchaseOrderItemMessage,
  PurchaseOrderMessageSendProblems,
  VendorMessagePageData,
  parsedPurchaseOrderItemIdParamSchema,
  sendPurchaseOrderItemMessageSchema,
} from "../../../_types";
import { formatDateTime, nameofFactory } from "../../../_helpers";

export const loader: LoaderFunction = async ({ params }) => {
  try {
    const currUser = await Auth.currentAuthenticatedUser();
    if (!currUser) return null;
  } catch (e) {
    return null;
  }

  const { purchaseOrderItemId } =
    parsedPurchaseOrderItemIdParamSchema.parse(params);

  const response = {
    headerData: await getPurchaseOrderItem(purchaseOrderItemId),
    messagesData: await getMessagesForPurchaseOrderItem(purchaseOrderItemId),
  };

  return response;
};

export const postMessage: ActionFunction = async ({ params, request }) => {
  const { purchaseOrderItemId } =
    parsedPurchaseOrderItemIdParamSchema.parse(params);

  const newMessage = sendPurchaseOrderItemMessageSchema.parse(
    Object.fromEntries(await request.formData())
  );

  const resp = await sendPurchaseOrderItemMessage(
    purchaseOrderItemId,
    newMessage
  );

  return resp;
};

export function VendorMessagesPage() {
  const { purchaseOrderItemId, vendorId } = useParams();
  const { headerData, messagesData } = useLoaderData() as VendorMessagePageData;
  const nameof = nameofFactory<PurchaseOrderItemMessage>();
  const submitMessage = useSubmit();
  const submitMessageErrors =
    useActionData() as PurchaseOrderMessageSendProblems;

  const columnHelper = createColumnHelper<PurchaseOrderItemMessage>();
  const columns = React.useMemo(
    () => [
      columnHelper.accessor((row) => row.senderName, {
        header: "From",
        id: nameof("senderName"),
      }),
      columnHelper.accessor((row) => row.messageContent, {
        header: "Message Contents",
        id: nameof("messageContent"),
      }),
      columnHelper.accessor((row) => row.sentTime, {
        header: "Sent",
        id: nameof("sentTime"),
        cell: (info) => formatDateTime(info.getValue()),
      }),
    ],
    [columnHelper, nameof]
  );

  const options: TableOptions<PurchaseOrderItemMessage> = {
    columns,
    data: messagesData,
    enableFilters: false, // remove if/when we need filters again
    getCoreRowModel: getCoreRowModel(),
  };
  const table = useReactTable(options);

  const handleOnSubmit = async (values: Record<string, any>) => {
    submitMessage(values, {
      method: "post",
      action: `vendor/${vendorId}/purchaseOrderItem/${purchaseOrderItemId}/messages`,
    });
  };

  return (
    <Container fluid>
      <Row>
        <Card>
          <Card.Body>
            <PurchaseOrderItemMessagingHeader headerData={headerData} />
          </Card.Body>
        </Card>
      </Row>

      <br />

      <Row>
        <SendPurchaseOrderItemMessageForm
          handleOnSubmit={handleOnSubmit}
          serverError={submitMessageErrors?.errors?.MessageContent}
        />
      </Row>

      <br />

      <Row>
        <Link to={`/vendor/${headerData?.vendorId}`}>
          <Button size="sm" type="button">
            <ArrowLeftCircleFill />
            {" Return to Vendor"}
          </Button>
        </Link>
      </Row>

      <br />

      <Row>
        <Card>
          <Card.Body>
            <BootstrapTable table={table} />
          </Card.Body>
        </Card>
      </Row>
    </Container>
  );
}
